<template>
    <div>
        <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Contact Submissions</h2>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="p-6 border-b border-creme-200 flex justify-between items-center">
                <h3 class="font-header text-[1.5rem] font-bold">Submissions ({{ pagination.total }})</h3>
                <button @click="() => loadContacts()"
                    class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                    Refresh
                </button>
            </div>
            <div class="max-h-[calc(100vh-300px)] overflow-y-auto">
                <div class="w-full overflow-x-auto">
                    <table class="w-full min-w-max">
                        <thead class="bg-green-700 text-white sticky top-0">
                            <tr>
                                <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold whitespace-nowrap">
                                    Name</th>
                                <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold whitespace-nowrap">
                                    Email</th>
                                <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold whitespace-nowrap">
                                    Subject</th>
                                <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold whitespace-nowrap">
                                    Date</th>
                                <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold whitespace-nowrap">
                                    Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="contact in contacts" :key="contact.id"
                                class="border-b border-creme-200 hover:bg-creme-50 cursor-pointer"
                                @click="viewContact(contact.id)">
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    contact.name }}</td>
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    contact.email }}</td>
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    contact.subject || 'N/A' }}</td>
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    formatDate(contact.created_at) }}</td>
                                <td class="px-4 py-3 whitespace-nowrap">
                                    <button @click.stop="deleteContact(contact.id)"
                                        class="text-red-600 hover:text-red-800 font-body text-[1.125rem] leading-[1.75]">
                                        Delete
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="contacts.length === 0 && !isLoading">
                                <td colspan="5"
                                    class="px-4 py-8 text-center text-gray-500 font-body text-[1.125rem] leading-[1.75]">
                                    No contact submissions yet</td>
                            </tr>
                            <tr v-if="isLoading">
                                <td colspan="5" class="px-4 py-8 text-center">
                                    <Loader size="medium" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="pagination.total_pages > 1"
                class="p-4 border-t border-creme-200 flex justify-between items-center">
                <button @click="previousPage" :disabled="!pagination.prev_page"
                    class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                    Previous
                </button>
                <span class="font-body text-[1.125rem] leading-[1.75] text-gray-700">
                    Page {{ pagination.page }} of {{ pagination.total_pages }}
                </span>
                <button @click="nextPage" :disabled="!pagination.next_page"
                    class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                    Next
                </button>
            </div>
        </div>

        <BaseModal v-model="showContactModal" content-class="max-w-2xl max-h-[90vh]">
            <h3 class="font-header text-[1.5rem] font-bold text-primary mb-4">Contact Details</h3>
            <div v-if="selectedContact" class="space-y-4">
                <div>
                    <label class="font-body text-[1.125rem] leading-[1.75] text-gray-600">Name</label>
                    <p class="font-body text-[1.125rem] leading-[1.75] text-primary">{{ selectedContact.name }}</p>
                </div>
                <div>
                    <label class="font-body text-[1.125rem] leading-[1.75] text-gray-600">Email</label>
                    <p class="font-body text-[1.125rem] leading-[1.75] text-primary">
                        <a :href="`mailto:${selectedContact.email}`" class="text-green-700 hover:underline">
                            {{ selectedContact.email }}
                        </a>
                    </p>
                </div>
                <div>
                    <label class="font-body text-[1.125rem] leading-[1.75] text-gray-600">Subject</label>
                    <p class="font-body text-[1.125rem] leading-[1.75] text-primary">{{ selectedContact.subject || 'N/A'
                        }}</p>
                </div>
                <div>
                    <label class="font-body text-[1.125rem] leading-[1.75] text-gray-600">Message</label>
                    <p class="font-body text-[1.125rem] leading-[1.75] text-primary whitespace-pre-wrap">{{
                        selectedContact.entry }}</p>
                </div>
                <div>
                    <label class="text-sm font-marketing font-semibold text-gray-600">Submitted</label>
                    <p class="text-lg font-sans text-primary">{{ formatDate(selectedContact.created_at) }}</p>
                </div>
            </div>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import BaseModal from './BaseModal.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import { formatDateTime } from '../../utils/date'

const contacts = ref([])
const selectedContact = ref(null)

const showContactModal = computed({
    get: () => !!selectedContact.value,
    set: (value) => {
        if (!value) selectedContact.value = null
    }
})
const { loading: isLoading, setLoading } = useLoadingState(false)
const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
    next_page: null,
    prev_page: null
})

const loadContacts = async (page) => {
    setLoading(true)
    try {
        const pageNum = typeof page === 'number' ? page : (pagination.value?.page || 1)
        const response = await adminService.getContactSubmissions(pageNum, pagination.value.limit)
        console.log('Contact submissions response:', response)
        if (response && response.data && response.pagination) {
            contacts.value = response.data || []
            pagination.value = {
                page: response.pagination.page,
                limit: response.pagination.limit,
                total: response.pagination.total,
                total_pages: response.pagination.total_pages,
                next_page: response.pagination.next_page,
                prev_page: response.pagination.prev_page
            }
        } else {
            console.error('Unexpected response format:', response)
            contacts.value = []
            alert('Failed to load contact submissions. Unexpected response format.')
        }
    } catch (error) {
        console.error('Failed to load contact submissions:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
        contacts.value = []
        alert(`Failed to load contact submissions: ${errorMessage}`)
    } finally {
        setLoading(false)
    }
}

const viewContact = async (id) => {
    try {
        const contact = await adminService.getContactSubmission(id)
        selectedContact.value = contact
    } catch (error) {
        console.error('Failed to load contact details:', error)
        alert('Failed to load contact details. Please try again.')
    }
}

const deleteContact = async (id) => {
    if (!confirm('Are you sure you want to delete this contact submission?')) {
        return
    }

    try {
        await adminService.deleteContactSubmission(id)
        contacts.value = contacts.value.filter(c => c.id !== id)
        pagination.value.total--
        if (selectedContact.value?.id === id) {
            selectedContact.value = null
        }
        if (contacts.value.length === 0 && pagination.value.page > 1) {
            loadContacts(pagination.value.page - 1)
        }
    } catch (error) {
        console.error('Failed to delete contact submission:', error)
        alert('Failed to delete contact submission. Please try again.')
    }
}

const nextPage = () => {
    if (pagination.value.next_page) {
        loadContacts(pagination.value.page + 1)
    }
}

const previousPage = () => {
    if (pagination.value.prev_page) {
        loadContacts(pagination.value.page - 1)
    }
}

const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A'
    try {
        return formatDateTime(dateValue) || 'N/A'
    } catch (error) {
        return 'N/A'
    }
}

onMounted(() => {
    loadContacts()
})
</script>

<style scoped></style>
